import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Appointment,
  CreateWaitlistEntry,
  WaitlistDepth,
  WaitlistEntry,
  WaitlistOfferResult,
  WaitlistStatus,
} from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';

/**
 * WS7 phase 4 — appointment-waitlist hooks. Distinct from the walk-in queue (data/queue.ts): the
 * queue is "I'm here now, in line today"; the waitlist is "book me if a slot frees up on this DATE
 * for this service (optionally this stylist)". A client joins when a day is full; a later
 * cancellation OFFERS the freed slot to the best-matching waiting entry via the queue's gap-offer
 * comms machinery (the API wires that into appointment-cancel — the console only reflects the state).
 *
 * Mirrors data/scheduling.ts / data/services.ts: TanStack Query over the generated client via
 * unwrap()/expectOk(). Sealed-book scoped exactly like appointments — RLS already isolates the rows
 * (a renter's GET only returns their own entries), so these hooks carry no extra scoping logic; the
 * UI layer surfaces the sealing legibly and gates who may remove what.
 *
 * Query keys are defined locally (the shared lib/query.ts `qk` is owned by the serial Integrate step);
 * Integrate may fold `wqk` into `qk` when wiring the diary depth indicator.
 */

/** Centralised waitlist query keys (local mirror of the lib/query.ts `qk` convention). */
export const wqk = {
  /** All entries, optionally narrowed by date/status — the cache key matches the filter. */
  list: (date?: string, status?: WaitlistStatus) => ['waitlist', { date, status }] as const,
  /** Per-day depth for the diary header / settings overview. */
  depth: ['waitlist', 'depth'] as const,
};

/**
 * List waitlist entries, optionally narrowed to one date and/or status. The API defaults to active
 * (waiting + offered) entries when status is omitted, matching the entity contract.
 */
export function useWaitlist(filter?: { date?: string; status?: WaitlistStatus }) {
  const date = filter?.date;
  const status = filter?.status;
  return useQuery({
    queryKey: wqk.list(date, status),
    queryFn: async (): Promise<WaitlistEntry[]> =>
      unwrap(
        await api.GET('/v1/waitlist', {
          params: {
            query: {
              ...(date ? { date } : {}),
              ...(status ? { status } : {}),
            },
          },
        }),
      ),
  });
}

/** Per-day waitlist depth (active waiting + offered counts) — what the diary header shows. */
export function useWaitlistDepth() {
  return useQuery({
    queryKey: wqk.depth,
    queryFn: async (): Promise<WaitlistDepth[]> => unwrap(await api.GET('/v1/waitlist/depth')),
  });
}

/** Depth for one specific date, or null when nothing is waiting/offered that day. */
export function useWaitlistDepthFor(date: string | undefined): WaitlistDepth | null {
  const { data } = useWaitlistDepth();
  if (!date) return null;
  return data?.find((d) => d.wantedDate === date) ?? null;
}

/**
 * Join the waitlist for a date/service (and optionally a specific stylist). The created entry
 * inherits the chosen stylist's book (sealed vs floor) server-side; "any stylist" inherits the
 * acting principal's own renter context. Invalidates the list + depth so the badge and panel
 * refresh together.
 */
export function useJoinWaitlist() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateWaitlistEntry): Promise<WaitlistEntry> =>
      // The generated body type over-requires nullable optionals; cast at the boundary — the
      // server re-validates against the same Zod schema (matches data/staff.ts / data/services.ts).
      unwrap(await api.POST('/v1/waitlist', { body: body as never })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });
}

/**
 * Cancel (remove) a waitlist entry. The API enforces sealed scoping (a renter may only remove their
 * own; a floor manager may remove anyone's) — the UI gates the control to match so a 403 is never the
 * first feedback.
 */
export function useRemoveWaitlistEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/waitlist/{id}', { params: { path: { id } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });
}

/* ──────────────────────────────────────────────────────────────────────────
 * WS10(a) — manual waitlist ACTIONS. The auto-offer fires on appointment-cancel
 * (a cancellation offers the freed slot to the best match); these three let an
 * operator drive the same lifecycle by hand: OFFER a waiting entry its slot,
 * CONVERT an offered entry into a real appointment, or EXPIRE (decline) an offer
 * so the next-best match is re-offered. The API enforces sealed scoping + the
 * appointments:write guard exactly like Remove/Create — the UI gates the controls
 * to match so a 403 is never the first feedback. All invalidate ['waitlist'] so
 * the panel + depth badge refresh together.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Manually offer a WAITING entry its slot — reuses the gap-offer comms path (consent-aware,
 * allowance-enforced), so a salon paused at its messaging cap is still flipped to "offered" but
 * `notified:false` (we pause, never auto-spend). Returns the offer result so the caller can surface
 * whether a text actually went out.
 */
export function useOfferWaitlistEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<WaitlistOfferResult> =>
      // Empty `{}` body (OfferWaitlistEntry is Record<string, never>); the generated body type is
      // the narrow empty-object shape, so cast at the boundary as the other waitlist hooks do.
      unwrap(
        await api.POST('/v1/waitlist/{id}/offer', { params: { path: { id } }, body: {} as never }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });
}

/**
 * Convert an OFFERED entry into a real appointment at the chosen start time (the entry only stores a
 * wanted DATE — the operator picks the concrete `startAt`). The service/staff come from the entry;
 * `endAt` is derived from the service server-side when omitted. We mint a fresh idempotencyKey per
 * call so a double-click can't book twice. Invalidates ['waitlist'] AND the appointments/diary keys
 * so the new booking appears on whichever day it lands.
 */
export function useConvertWaitlistEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      startAt: string;
      endAt?: string | null;
    }): Promise<Appointment> =>
      unwrap(
        await api.POST('/v1/waitlist/{id}/convert', {
          params: { path: { id: vars.id } },
          // ConvertWaitlistEntry = { startAt, endAt?, idempotencyKey? }; mint the key once here so a
          // retry of the SAME mutation is idempotent. Cast at the boundary like the other hooks.
          body: {
            startAt: vars.startAt,
            ...(vars.endAt ? { endAt: vars.endAt } : {}),
            idempotencyKey: crypto.randomUUID(),
          } as never,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['waitlist'] });
      // The new appointment lands on the converted date — invalidate the whole appointments prefix
      // (keyed per-date as qk.appointments(date)) so the diary refreshes regardless of which day.
      void qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/**
 * Expire (decline) an OFFERED entry — the enum has no 'declined' state, so a decline IS an expire:
 * the offer is released and the next-best match is re-offered (the `reoffer` in the result). Returns
 * the released entry + any re-offer so the caller can reflect what happened.
 */
export function useExpireWaitlistEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      id: string,
    ): Promise<{ entry: WaitlistEntry; reoffer: WaitlistOfferResult | null }> =>
      // Empty `{}` body (ExpireWaitlistEntry is Record<string, never>) — cast at the boundary.
      unwrap(
        await api.POST('/v1/waitlist/{id}/expire', { params: { path: { id } }, body: {} as never }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });
}
